import React, { useEffect, useState, useRef } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, lookupPostal } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import SignaturePad from "@/components/SignaturePad";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Package, Loader2, Search, Paperclip, MapPin, CheckCircle2, PenLine, Camera, X, Download, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export const DOC_CATS = [
  { key: "albaran", label: "Albarán de recogida/entrega" },
  { key: "listin", label: "Listín" },
  { key: "awb", label: "AWB" },
  { key: "levante", label: "Levante" },
  { key: "adicional", label: "Docs adicionales" },
];

export const SERVICE_TYPES = [
  { k: "transporte", l: "Transporte" },
  { k: "mensajeria", l: "Mensajería" },
  { k: "inspeccion", l: "Inspección de aduana" },
  { k: "varios", l: "Servicios varios" },
];

export const DETAIL_FIELDS = [
  { k: "company", l: "Nombre de la empresa" },
  { k: "address", l: "Dirección completa" },
  { k: "postal_code", l: "Código postal" },
  { k: "poblacion", l: "Población" },
  { k: "ciudad", l: "Ciudad" },
  { k: "schedule", l: "Horario" },
  { k: "contact_name", l: "Persona de contacto" },
  { k: "contact_phone", l: "Teléfono de contacto" },
  { k: "reference", l: "Referencia" },
  { k: "notes", l: "Observaciones" },
];

const emptyDetail = () => ({ company: "", address: "", postal_code: "", poblacion: "", ciudad: "", schedule: "", contact_name: "", contact_phone: "", reference: "", notes: "" });

export default function Shipments() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [ships, setShips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [soonOnly, setSoonOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companySel, setCompanySel] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [tmOpen, setTmOpen] = useState(false);
  const [tmNext, setTmNext] = useState("");
  const [tmInput, setTmInput] = useState("");
  const [tmBusy, setTmBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState({});
  const [proofShip, setProofShip] = useState(null);
  const [proofNote, setProofNote] = useState("");
  const [proofPhoto, setProofPhoto] = useState(null);
  const [proofBusy, setProofBusy] = useState(false);
  const sigRef = useRef(null);
  const [addrBook, setAddrBook] = useState([]);
  const [form, setForm] = useState({ client_id: "", client_nif: "", service_type: "transporte", reference: "", cargo_nature: "", weight: "", volume_m3: "", assigned_driver_id: "", request_date: new Date().toISOString().slice(0, 10), pickup_estimated: "", estimated_delivery: "", pickup: emptyDetail(), delivery: emptyDetail(), measures: [{ length: "", width: "", height: "", packages: "" }] });

  const dayLabel = (d) => {
    if (!d) return null;
    const dt = new Date(d + "T00:00:00");
    if (isNaN(dt.getTime())) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((dt - today) / 86400000);
    if (diff === 0) return "Hoy";
    if (diff === 1) return "Mañana";
    if (diff === -1) return "Ayer";
    return ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"][dt.getDay()];
  };
  const dayCls = (lbl) =>
    lbl === "Hoy" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
      : lbl === "Mañana" ? "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
        : lbl === "Ayer" ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
          : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300";
  const routeShort = (s) => {
    const part = (x) => [(x || {}).company, (x || {}).ciudad || (x || {}).poblacion].filter(Boolean).join(", ");
    const o = part(s.pickup_details) || s.origin || "—";
    const d = part(s.delivery_details) || s.destination || "—";
    return `${o} → ${d}`;
  };
  const bultosOf = (s) => (s.measures || []).reduce((a, m) => a + (parseInt(m.packages) || 0), 0) || s.packages || 0;

  const load = () => {
    setLoading(true);
    api.get("/shipments").then((r) => setShips(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (user.role === "admin") {
      api.get("/users?role=client").then((r) => setClients(r.data)).catch(() => {});
      api.get("/users?role=driver").then((r) => setDrivers(r.data)).catch(() => {});
      api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    } else if (user.role === "client") {
      api.get("/address-book").then((r) => setAddrBook(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (user.role === "admin" && form.client_id) {
      api.get(`/address-book?client_id=${form.client_id}`).then((r) => setAddrBook(r.data)).catch(() => setAddrBook([]));
    }
  }, [form.client_id]);

  const onCompany = (section, v) => {
    setDetail(section, "company", v);
    const match = addrBook.find((e) => (e.company || "").trim().toLowerCase() === v.trim().toLowerCase());
    if (match) {
      const filled = {};
      DETAIL_FIELDS.forEach((f) => { filled[f.k] = match[f.k] || ""; });
      setForm((f) => ({ ...f, [section]: { ...filled } }));
    }
  };

  const applyAddress = (section, a) => {
    const filled = {};
    DETAIL_FIELDS.forEach((f) => { filled[f.k] = a[f.k] || ""; });
    setForm((f) => ({ ...f, [section]: { ...filled } }));
  };

  const [albaranBusy, setAlbaranBusy] = useState(false);
  const [albaranFile, setAlbaranFile] = useState(null);
  const mergeDetail = (base, incoming) => {
    const o = { ...base };
    Object.keys(incoming || {}).forEach((k) => { if (incoming[k]) o[k] = incoming[k]; });
    return o;
  };
  const parseAlbaran = async (fileObj) => {
    if (!fileObj) return;
    setAlbaranBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post("/albaran/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        reference: data.reference || f.reference,
        weight: data.weight || f.weight,
        volume_m3: data.volume_m3 || f.volume_m3,
        pickup_estimated: data.pickup_estimated || f.pickup_estimated,
        estimated_delivery: data.estimated_delivery || f.estimated_delivery,
        measures: (data.measures && data.measures.length) ? data.measures : f.measures,
        pickup: mergeDetail(f.pickup, data.pickup),
        delivery: mergeDetail(f.delivery, data.delivery),
      }));
      setAlbaranFile(fileObj);
      toast.success("Datos autocompletados con IA. Revísalos antes de guardar.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el albarán");
    } finally { setAlbaranBusy(false); }
  };

  const create = async () => {
    if (user.role === "admin" && !form.client_id) { toast.error("Selecciona empresa y operativo"); return; }
    setBusy(true);
    try {
      const origin = [form.pickup.company, form.pickup.address, form.pickup.postal_code].filter(Boolean).join(", ");
      const destination = [form.delivery.company, form.delivery.address, form.delivery.postal_code].filter(Boolean).join(", ");
      const measures = form.measures.filter((m) => m.length || m.width || m.height || m.packages);
      const totalPackages = form.measures.reduce((a, m) => a + (parseInt(m.packages) || 0), 0) || 1;
      const payload = {
        client_id: user.role === "client" ? "" : form.client_id,
        client_nif: form.client_nif || null,
        service_type: form.service_type, origin, destination,
        description: "", cargo_nature: form.cargo_nature, reference: form.reference,
        weight: parseFloat(form.weight) || 0,
        packages: totalPackages,
        volume_m3: form.volume_m3 !== "" && form.volume_m3 != null ? parseFloat(form.volume_m3) : null,
        measures,
        assigned_driver_id: user.role === "client" ? null : (form.assigned_driver_id || null),
        pickup_estimated: form.pickup_estimated || null,
        estimated_delivery: form.estimated_delivery || null,
        request_date: form.request_date || null,
        pickup_details: form.pickup, delivery_details: form.delivery,
      };
      const { data: created } = await api.post("/shipments", payload);
      const uploads = [];
      if (albaranFile) {
        const fd = new FormData();
        fd.append("category", "albaran");
        fd.append("file", albaranFile);
        uploads.push(api.post(`/shipments/${created.id}/documents`, fd));
      }
      for (const c of DOC_CATS) {
        const list = files[c.key];
        if (list) for (const f of Array.from(list)) {
          const fd = new FormData();
          fd.append("category", c.key);
          fd.append("file", f);
          uploads.push(api.post(`/shipments/${created.id}/documents`, fd));
        }
      }
      if (uploads.length) await Promise.all(uploads);
      toast.success(user.role === "client" ? "Recogida solicitada correctamente" : "Envío creado");
      setOpen(false);
      setCompanySel("");
      setForm({ client_id: "", client_nif: "", service_type: "transporte", reference: "", cargo_nature: "", weight: "", volume_m3: "", assigned_driver_id: "", request_date: new Date().toISOString().slice(0, 10), pickup_estimated: "", estimated_delivery: "", pickup: emptyDetail(), delivery: emptyDetail(), measures: [{ length: "", width: "", height: "", packages: "" }] });
      setFiles({});
      setAlbaranFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setDetail = (section, k, v) => setForm((f) => ({ ...f, [section]: { ...f[section], [k]: v } }));

  const onPostal = async (section, v) => {
    setDetail(section, "postal_code", v);
    const res = await lookupPostal(v);
    if (res) setForm((f) => ({ ...f, [section]: { ...f[section], postal_code: v, poblacion: f[section].poblacion || res.poblacion, ciudad: f[section].ciudad || res.ciudad } }));
  };

  const computeVol = (measures) => {
    let v = 0;
    measures.forEach((m) => { const l = parseFloat(m.length), w = parseFloat(m.width), h = parseFloat(m.height), p = parseInt(m.packages) || 1; if (l > 0 && w > 0 && h > 0) v += (l * w * h) / 1000000 * p; });
    return v;
  };
  const setMeasure = (i, k, v) => {
    const measures = form.measures.map((m, idx) => idx === i ? { ...m, [k]: v } : m);
    const vol = computeVol(measures);
    setForm({ ...form, measures, volume_m3: vol > 0 ? Number(vol.toFixed(3)) : form.volume_m3 });
  };
  const addMeasure = () => { if (form.measures.length < 5) setForm({ ...form, measures: [...form.measures, { length: "", width: "", height: "", packages: "" }] }); };
  const removeMeasure = (i) => {
    const measures = form.measures.filter((_, idx) => idx !== i);
    const vol = computeVol(measures);
    setForm({ ...form, measures, volume_m3: vol > 0 ? Number(vol.toFixed(3)) : form.volume_m3 });
  };

  const filtered = ships.filter((s) => {
    const matchesQ = !q || s.tracking_number.toLowerCase().includes(q.toLowerCase()) ||
      (s.destination || "").toLowerCase().includes(q.toLowerCase()) ||
      (s.client_name || "").toLowerCase().includes(q.toLowerCase()) ||
      (s.client_company || "").toLowerCase().includes(q.toLowerCase()) ||
      (s.reference || "").toLowerCase().includes(q.toLowerCase());
    const matchesStatus = statusFilter === "all" ? true
      : statusFilter === "not_delivered" ? s.status !== "delivered"
      : s.status === statusFilter;
    const matchesService = serviceFilter === "all" || (s.service_type || "transporte") === serviceFilter;
    const todayISO = new Date().toLocaleDateString("en-CA");
    const tomorrowISO = new Date(Date.now() + 86400000).toLocaleDateString("en-CA");
    const matchesSoon = !soonOnly || (s.pickup_estimated === todayISO || s.pickup_estimated === tomorrowISO);
    const created = (s.created_at || "").slice(0, 10);
    const matchesFrom = !dateFrom || (created && created >= dateFrom);
    const matchesTo = !dateTo || (created && created <= dateTo);
    return matchesQ && matchesStatus && matchesService && matchesSoon && matchesFrom && matchesTo;
  });

  const STATUS_LABEL = { pending: "Pendiente", in_transit: "En tránsito", delivered: "Entregado", exception: "Incidencia" };
  const exportCSV = () => {
    if (filtered.length === 0) { toast.error("No hay envíos para exportar"); return; }
    const isAdmin = user.role === "admin";
    const headers = ["Tracking", "Cliente", "Empresa", "Servicio", "Referencia", "Origen", "Destino", "Bultos", "Peso (kg)", "Volumen (m3)", "Coste (EUR)",
      ...(isAdmin ? ["Colaborador", "Coste colaborador (EUR)", "Margen (EUR)", "Observaciones internas"] : []),
      "Estado", "Conductor", "Fecha"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const svcLabel = (k) => (SERVICE_TYPES.find((t) => t.k === (k || "transporte")) || { l: "Transporte" }).l;
    const margin = (s) => {
      if (s.cost == null && s.collaborator_cost == null) return "";
      return ((Number(s.cost) || 0) - (Number(s.collaborator_cost) || 0)).toFixed(2);
    };
    const rows = filtered.map((s) => [
      s.tracking_number || "", s.client_name || "", s.client_company || "", svcLabel(s.service_type), s.reference || "",
      s.origin || "", s.destination || "", s.packages ?? "", s.weight ?? "",
      s.volume_m3 ?? "", s.cost ?? "",
      ...(isAdmin ? [s.collaborator ?? "", s.collaborator_cost ?? "", margin(s), s.internal_notes ?? ""] : []),
      STATUS_LABEL[s.status] || s.status || "",
      s.driver_name || "", (s.created_at || "").slice(0, 10),
    ].map(esc).join(","));
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `envios_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} envío(s) exportado(s)`);
  };

  const exportXLSX = async () => {
    if (filtered.length === 0) { toast.error("No hay envíos para exportar"); return; }
    try {
      const res = await api.post("/shipments/export.xlsx", { ids: filtered.map((s) => s.id) }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturacion_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filtered.length} envío(s) exportado(s) a Excel`);
    } catch { toast.error("No se pudo generar el Excel"); }
  };

  const openTm = async () => {
    setTmOpen(true);
    try { const { data } = await api.get("/tm-counter"); setTmNext(data.next_number); setTmInput(data.next_number); }
    catch { setTmNext(""); }
  };
  const saveTm = async () => {
    setTmBusy(true);
    try {
      const { data } = await api.put("/tm-counter", { next_number: tmInput });
      setTmNext(data.next_number);
      toast.success(`Hecho. El próximo envío será ${data.next_number}`);
      setTmOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setTmBusy(false); }
  };

  const openProof = (s) => { setProofShip(s); setProofNote(""); setProofPhoto(null); };

  const submitProof = async () => {
    const sigBlob = await new Promise((res) => {
      if (sigRef.current && !sigRef.current.isEmpty()) sigRef.current.toBlob((b) => res(b));
      else res(null);
    });
    if (!sigBlob && !proofPhoto) { toast.error("Añade una firma o una foto"); return; }
    setProofBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", proofNote);
      if (proofPhoto) fd.append("photo", proofPhoto);
      if (sigBlob) fd.append("signature", new File([sigBlob], "firma.png", { type: "image/png" }));
      await api.post(`/shipments/${proofShip.id}/delivery-proof`, fd);
      toast.success("Entrega confirmada");
      setProofShip(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProofBusy(false); }
  };

  const inputCls = "w-full bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight";

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <PageHeader
          title={user.role === "driver" ? "Mis Rutas" : user.role === "client" ? "Mis Envíos" : "Envíos"}
          subtitle={`${ships.length} registro(s)`}
          action={user.role !== "driver" && (
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <button data-testid="tm-counter-btn" onClick={openTm}
                  className="border border-corp text-corp hover:bg-corp hover:text-white font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors">
                  <Package className="w-4 h-4" /> Numeración
                </button>
              )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="create-shipment-btn" className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors">
                  <Plus className="w-4 h-4" /> {user.role === "client" ? "Nueva recogida" : "Nuevo envío"}
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-card max-w-2xl">
                <DialogHeader><DialogTitle className="font-display font-black uppercase tracking-tight">{user.role === "client" ? "Solicitar Recogida" : "Nuevo Envío"}</DialogTitle></DialogHeader>
                <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
                  {user.role === "admin" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold uppercase text-muted-foreground">Cliente (empresa) *</label>
                        <select data-testid="ship-company" className={inputCls} value={companySel}
                          onChange={(e) => { const co = e.target.value; const match = companies.find((x) => x.company === co); setCompanySel(co); setForm({ ...form, client_id: "", client_nif: match?.nif || "" }); }}>
                          <option value="">Seleccionar empresa *</option>
                          {[...new Set(clients.map((c) => (c.company || "").trim() || "Sin empresa"))].sort().map((co) => <option key={co} value={co}>{co}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase text-muted-foreground">Operativo (quien solicita) *</label>
                        <select data-testid="ship-operativo" className={inputCls} value={form.client_id} disabled={!companySel}
                          onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                          <option value="">Seleccionar operativo *</option>
                          {clients.filter((c) => ((c.company || "").trim() || "Sin empresa") === companySel).map((o) => <option key={o.id} value={o.id}>{o.name}{o.email ? ` (${o.email})` : ""}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  {user.role === "admin" && (
                    <div>
                      <label className="text-[10px] font-bold uppercase text-muted-foreground">NIF/CIF del cliente (cargador) — se rellena solo y sale en albarán y DeCA</label>
                      <input data-testid="ship-client-nif" className={inputCls} value={form.client_nif} placeholder="Ej: B12345678" onChange={(e) => setForm({ ...form, client_nif: e.target.value })} />
                    </div>
                  )}
                  <label data-testid="albaran-dropzone" className={`border-2 border-dashed p-4 flex flex-col items-center text-center cursor-pointer transition-colors ${albaranBusy ? "border-corp bg-corp/10" : "border-corp/40 bg-corp/5 hover:bg-corp/10"}`}>
                    <div className="flex items-center gap-2 text-sm font-bold uppercase text-corp">
                      {albaranBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {albaranBusy ? "Leyendo albarán con IA…" : "Subir albarán y autocompletar con IA"}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">PDF o foto del albarán. La IA rellenará los campos; revísalos antes de guardar.</p>
                    {albaranFile && <p data-testid="albaran-attached" className="text-[10px] font-bold text-emerald-600 mt-1">✓ Se adjuntará "{albaranFile.name}" al envío</p>}
                    <input data-testid="albaran-upload" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={albaranBusy}
                      onChange={(e) => { const fl = e.target.files[0]; e.target.value = ""; parseAlbaran(fl); }} />
                  </label>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Tipo de servicio</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {SERVICE_TYPES.map((t) => (
                        <button key={t.k} type="button" data-testid={`service-${t.k}`} onClick={() => setForm({ ...form, service_type: t.k })}
                          className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${form.service_type === t.k ? "bg-freight border-freight text-black" : "border-input text-muted-foreground hover:border-freight hover:text-freight"}`}>
                          {t.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Referencia (para tu facturación)</label>
                    <input data-testid="ship-reference" className={inputCls} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Tu referencia interna (opcional)" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Naturaleza de la mercancía</label>
                    <input data-testid="ship-cargo-nature" className={inputCls} value={form.cargo_nature} onChange={(e) => setForm({ ...form, cargo_nature: e.target.value })} placeholder="Ej: Palets de material eléctrico" />
                  </div>
                  <div className="border border-border p-3 space-y-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Datos de la mercancía</div>
                      <div className="w-28"><label className="text-[10px] font-bold uppercase text-muted-foreground">Kilos</label><input data-testid="ship-weight" type="number" className={inputCls} value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></div>
                    </div>
                    <div className="space-y-2">
                      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-[10px] font-bold uppercase text-muted-foreground">
                        <span>Largo (cm)</span><span>Ancho (cm)</span><span>Alto (cm)</span><span>Bultos</span><span></span>
                      </div>
                      {form.measures.map((m, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                          <input data-testid={`measure-length-${i}`} type="number" className={inputCls} value={m.length} onChange={(e) => setMeasure(i, "length", e.target.value)} />
                          <input data-testid={`measure-width-${i}`} type="number" className={inputCls} value={m.width} onChange={(e) => setMeasure(i, "width", e.target.value)} />
                          <input data-testid={`measure-height-${i}`} type="number" className={inputCls} value={m.height} onChange={(e) => setMeasure(i, "height", e.target.value)} />
                          <input data-testid={`measure-packages-${i}`} type="number" className={inputCls} value={m.packages} onChange={(e) => setMeasure(i, "packages", e.target.value)} />
                          {form.measures.length > 1 ? <button type="button" onClick={() => removeMeasure(i)} className="text-red-500 flex justify-center"><X className="w-4 h-4" /></button> : <span />}
                        </div>
                      ))}
                      {form.measures.length < 5 && <button type="button" data-testid="add-measure-btn" onClick={addMeasure} className="text-xs font-bold uppercase text-corp hover:underline">+ Añadir línea de medidas</button>}
                    </div>
                    <div><label className="text-[10px] font-bold uppercase text-muted-foreground">Volumen cúbico (m³) — automático o manual</label><input data-testid="ship-volume" type="number" className={inputCls} value={form.volume_m3} onChange={(e) => setForm({ ...form, volume_m3: e.target.value })} placeholder="Se calcula con las medidas, o introdúcelo a mano" /></div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {[["pickup", "Recogida"], ["delivery", "Entrega"]].map(([sec, title]) => (
                      <div key={sec} className="border border-border p-3 space-y-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-corp">{title}</div>
                        {addrBook.length > 0 && (
                          <select data-testid={`${sec}-address-select`} className={inputCls} value=""
                            onChange={(e) => { const a = addrBook.find((x) => x.id === e.target.value); if (a) applyAddress(sec, a); }}>
                            <option value="">— Elegir dirección guardada —</option>
                            {addrBook.map((a) => <option key={a.id} value={a.id}>{a.company}</option>)}
                          </select>
                        )}
                        {DETAIL_FIELDS.map((f) => (
                          <div key={f.k}>
                            <label className="text-[10px] font-bold uppercase text-muted-foreground">{f.l}</label>
                            {f.k === "notes" ? (
                              <textarea data-testid={`${sec}-${f.k}`} className={inputCls} value={form[sec][f.k]} onChange={(e) => setDetail(sec, f.k, e.target.value)} />
                            ) : f.k === "company" ? (
                              <>
                                <input data-testid={`${sec}-${f.k}`} className={inputCls} list={`${sec}-companies`} value={form[sec][f.k]} onChange={(e) => onCompany(sec, e.target.value)} placeholder="Escribe y se autocompleta si ya existe" />
                                <datalist id={`${sec}-companies`}>
                                  {addrBook.map((a, idx) => <option key={idx} value={a.company} />)}
                                </datalist>
                              </>
                            ) : (
                              <input data-testid={`${sec}-${f.k}`} className={inputCls} value={form[sec][f.k]} onChange={(e) => f.k === "postal_code" ? onPostal(sec, e.target.value) : setDetail(sec, f.k, e.target.value)} />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {user.role === "admin" && (
                    <select data-testid="ship-driver" className={inputCls} value={form.assigned_driver_id} onChange={(e) => setForm({ ...form, assigned_driver_id: e.target.value })}>
                      <option value="">Asignar conductor (opcional)</option>
                      {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fecha de petición</label>
                      <input data-testid="ship-request-date" type="date" className={inputCls} value={form.request_date} onChange={(e) => setForm({ ...form, request_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fecha del servicio (recogida)</label>
                      <input data-testid="ship-pickup-eta" type="date" className={inputCls} value={form.pickup_estimated} onChange={(e) => setForm({ ...form, pickup_estimated: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Entrega estimada</label>
                      <input data-testid="ship-eta" type="date" className={inputCls} value={form.estimated_delivery} onChange={(e) => setForm({ ...form, estimated_delivery: e.target.value })} />
                    </div>
                  </div>
                  <div className="border border-border p-3 space-y-2.5">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Documentos adjuntos</div>
                    {DOC_CATS.map((c) => (
                      <div key={c.key} className="flex flex-col gap-1">
                        <label className="text-xs font-semibold">{c.label}</label>
                        <input data-testid={`file-${c.key}`} type="file" multiple onChange={(e) => setFiles({ ...files, [c.key]: e.target.files })}
                          className="text-xs file:mr-2 file:border file:border-input file:bg-muted file:px-2 file:py-1 file:text-xs file:font-bold file:uppercase file:cursor-pointer" />
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="save-shipment-btn" onClick={create} disabled={busy} className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          )}
        />

        {tmOpen && (
          <Dialog open={tmOpen} onOpenChange={setTmOpen}>
            <DialogContent className="rounded-none bg-card max-w-md">
              <DialogHeader><DialogTitle className="font-display font-black uppercase">Numeración TM</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">El próximo envío recibirá el número <span className="font-mono font-bold text-corp">{tmNext || "…"}</span>. Puedes fijar aquí el número de salida; a partir de él, el programa numerará automáticamente.</p>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Número de salida (ej. TM260900)</label>
                  <input data-testid="tm-next-input" className={inputCls + " font-mono"} value={tmInput} onChange={(e) => setTmInput(e.target.value)} placeholder="TM260900" />
                </div>
              </div>
              <DialogFooter>
                <button data-testid="save-tm-btn" onClick={saveTm} disabled={tmBusy} className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
                  {tmBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar numeración
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <div className="flex flex-col gap-3 mb-4">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="search-shipments" className="w-full bg-card border border-input rounded-none pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-freight"
              placeholder="Buscar por TM, referencia, destino, cliente o empresa" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[{ v: "all", l: "Todos" }, { v: "not_delivered", l: "Pendientes" }, { v: "pending", l: "Pendiente" }, { v: "in_transit", l: "En tránsito" }, { v: "delivered", l: "Entregado" }].map((f) => (
              <button key={f.v} data-testid={`filter-${f.v}`} onClick={() => setStatusFilter(f.v)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${statusFilter === f.v ? "bg-freight border-freight text-black" : "border-input text-muted-foreground hover:text-freight hover:border-freight"}`}>
                {f.l}
              </button>
            ))}
            {user.role !== "driver" && (
              <button data-testid="filter-soon" onClick={() => setSoonOnly((v) => !v)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${soonOnly ? "bg-emerald-600 border-emerald-600 text-white" : "border-input text-muted-foreground hover:text-emerald-600 hover:border-emerald-600"}`}>
                Hoy y mañana
              </button>
            )}
            {user.role !== "driver" && (
              <select data-testid="service-filter" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide border border-input bg-card text-muted-foreground focus:outline-none focus:ring-2 focus:ring-freight">
                <option value="all">Todos los servicios</option>
                {SERVICE_TYPES.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
              </select>
            )}
            {user.role !== "driver" && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase text-muted-foreground">Desde</span>
                <input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-xs font-bold border border-input bg-card text-muted-foreground focus:outline-none focus:ring-2 focus:ring-freight" />
                <span className="text-[10px] font-bold uppercase text-muted-foreground">Hasta</span>
                <input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-xs font-bold border border-input bg-card text-muted-foreground focus:outline-none focus:ring-2 focus:ring-freight" />
                {(dateFrom || dateTo) && (
                  <button data-testid="clear-dates" onClick={() => { setDateFrom(""); setDateTo(""); }}
                    className="px-2 py-1.5 text-xs font-bold uppercase border border-input text-muted-foreground hover:text-freight hover:border-freight transition-colors">Limpiar</button>
                )}
                <button data-testid="period-this-month" onClick={() => { const n = new Date(); setDateFrom(new Date(n.getFullYear(), n.getMonth(), 1).toLocaleDateString("en-CA")); setDateTo(new Date(n.getFullYear(), n.getMonth() + 1, 0).toLocaleDateString("en-CA")); }}
                  className="px-2 py-1.5 text-xs font-bold uppercase border border-corp text-corp hover:bg-corp hover:text-white transition-colors">Este mes</button>
                <button data-testid="period-last-month" onClick={() => { const n = new Date(); setDateFrom(new Date(n.getFullYear(), n.getMonth() - 1, 1).toLocaleDateString("en-CA")); setDateTo(new Date(n.getFullYear(), n.getMonth(), 0).toLocaleDateString("en-CA")); }}
                  className="px-2 py-1.5 text-xs font-bold uppercase border border-corp text-corp hover:bg-corp hover:text-white transition-colors">Mes anterior</button>
              </div>
            )}
            {user.role !== "driver" && (
              <button data-testid="export-csv-btn" onClick={exportCSV}
                className="ml-auto px-3 py-1.5 text-xs font-bold uppercase tracking-wide border border-corp text-corp hover:bg-corp hover:text-white transition-colors flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Exportar CSV
              </button>
            )}
            {user.role === "admin" && (
              <button data-testid="export-xlsx-btn" onClick={exportXLSX}
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Exportar Excel
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No hay envíos.</p>
          </div>
        ) : user.role === "driver" ? (
          <div className="grid gap-3">
            {filtered.map((s) => (
              <div key={s.id} data-testid={`ship-card-${s.id}`} className="bg-card border border-border p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-mono font-bold text-freight">{s.tracking_number || "SIN ASIGNAR"}</div>
                    <div className="text-sm mt-0.5">{s.description}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{routeShort(s)}</div>
                    <div className="text-xs text-muted-foreground mt-1">{bultosOf(s)} bultos · {s.weight || 0} kg · {s.volume_m3 != null ? s.volume_m3 : 0} m³</div>
                    <div className="text-xs text-muted-foreground mt-1">Cliente: <span className="font-semibold text-foreground">{s.client_name}</span>{s.client_company ? ` · ${s.client_company}` : ""}</div>
                    <div className="text-xs mt-1"><span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border border-corp/40 text-corp bg-corp/5">{(SERVICE_TYPES.find((t) => t.k === (s.service_type || "transporte")) || { l: "Transporte" }).l}</span></div>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => nav(`/shipments/${s.id}`)} data-testid={`ship-detail-${s.id}`}
                    className="flex-1 border border-input font-bold uppercase text-xs py-3 hover:bg-muted transition-colors">Ver detalle</button>
                  {s.status !== "delivered" && !s.delivery_proof && (
                    <button onClick={() => openProof(s)} data-testid={`confirm-delivery-${s.id}`}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm py-3 flex items-center justify-center gap-2 transition-colors">
                      <CheckCircle2 className="w-5 h-5" /> Confirmar entrega
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-border overflow-x-auto bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-3 px-4 font-bold">Tracking</th>
                  <th className="py-3 px-4 font-bold">Cliente</th>
                  <th className="py-3 px-4 font-bold">Servicio</th>
                  <th className="py-3 px-4 font-bold hidden sm:table-cell">Mercancía</th>
                  <th className="py-3 px-4 font-bold hidden sm:table-cell">F. servicio</th>
                  {user.role === "admin" && <th className="py-3 px-4 font-bold">Coste</th>}
                  <th className="py-3 px-4 font-bold hidden md:table-cell">Ruta</th>
                  <th className="py-3 px-4 font-bold hidden lg:table-cell">Conductor</th>
                  <th className="py-3 px-4 font-bold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`shipment-row-${s.id}`} onClick={() => nav(`/shipments/${s.id}`)}
                    className="border-b border-border last:border-0 hover:bg-freight/5 cursor-pointer transition-colors">
                    <td className="py-3 px-4 font-mono font-bold text-freight">{s.tracking_number || "— sin asignar"}</td>
                    <td className="py-3 px-4">
                      <div className="font-bold text-[15px] leading-tight">{s.client_company || s.client_name}</div>
                      {s.client_company && s.client_name ? <div className="text-xs text-muted-foreground mt-0.5">{s.client_name}</div> : null}
                    </td>
                    <td className="py-3 px-4">
                      <span data-testid={`row-service-${s.id}`} className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border border-corp/40 text-corp bg-corp/5">
                        {(SERVICE_TYPES.find((t) => t.k === (s.service_type || "transporte")) || { l: "Transporte" }).l}
                      </span>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell text-xs" data-testid={`row-cargo-${s.id}`}>
                      <div className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                        <span><b className="text-foreground">{bultosOf(s)}</b> blt</span><span>·</span>
                        <span><b className="text-foreground">{s.weight || 0}</b> kg</span><span>·</span>
                        <span><b className="text-foreground">{s.volume_m3 != null ? s.volume_m3 : 0}</b> m³</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell text-muted-foreground text-xs" data-testid={`row-service-date-${s.id}`}>
                      {s.pickup_estimated ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{s.pickup_estimated}</span>
                          {dayLabel(s.pickup_estimated) && (
                            <span data-testid={`row-service-day-${s.id}`} className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${dayCls(dayLabel(s.pickup_estimated))}`}>{dayLabel(s.pickup_estimated)}</span>
                          )}
                        </div>
                      ) : "—"}
                    </td>
                    {user.role === "admin" && (
                      <td className="py-3 px-4" data-testid={`row-cost-${s.id}`}>
                        {s.cost != null
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"><Check className="w-3 h-3" /> {s.cost} €</span>
                          : <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">Sin coste</span>}
                      </td>
                    )}
                    <td className="py-3 px-4 hidden md:table-cell text-muted-foreground">{routeShort(s)}</td>
                    <td className="py-3 px-4 hidden lg:table-cell text-muted-foreground">{s.driver_name || "—"}</td>
                    <td className="py-3 px-4"><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={!!proofShip} onOpenChange={(o) => { if (!o) setProofShip(null); }}>
          <DialogContent className="rounded-none bg-card">
            <DialogHeader><DialogTitle className="font-display font-black uppercase">Confirmar Entrega{proofShip ? ` · ${proofShip.tracking_number || "S/N"}` : ""}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1 mb-1"><PenLine className="w-3 h-3" /> Firma del receptor</label>
                {proofShip && <SignaturePad ref={sigRef} />}
                <button type="button" onClick={() => sigRef.current && sigRef.current.clear()} className="text-xs text-muted-foreground hover:text-freight mt-1 transition-colors">Borrar firma</button>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1 mb-1"><Camera className="w-3 h-3" /> Foto de entrega</label>
                <input data-testid="card-proof-photo" type="file" accept="image/*" capture="environment" onChange={(e) => setProofPhoto(e.target.files[0])}
                  className="text-xs w-full file:mr-2 file:border file:border-input file:bg-muted file:px-2 file:py-1.5 file:text-xs file:font-bold file:uppercase file:cursor-pointer" />
              </div>
              <textarea data-testid="card-proof-note" className={inputCls} placeholder="Nota (opcional): nombre de quien recibe..." value={proofNote} onChange={(e) => setProofNote(e.target.value)} />
            </div>
            <DialogFooter>
              <button data-testid="card-save-proof-btn" onClick={submitProof} disabled={proofBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60">
                {proofBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
